import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, deleteToken, onMessage } from 'firebase/messaging';

// Firebase configuration from environment variables
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

let app = null;
let messaging = null;

// Token refresh callback storage
const tokenRefreshCallbacks = [];

/**
 * Initialize Firebase app and messaging
 * Should be called once at app startup
 */
export const initializeFirebase = () => {
    if (process.env.REACT_APP_FIREBASE_DISABLED === 'true') {
        console.info('[Firebase] Disabled via REACT_APP_FIREBASE_DISABLED');
        return false;
    }

    // Validate required config
    if (!firebaseConfig.apiKey || !firebaseConfig.messagingSenderId) {
        console.error('[Firebase] Missing required configuration (apiKey or messagingSenderId)');
        return false;
    }

    try {
        app = initializeApp(firebaseConfig);

        // Check if browser supports notifications
        if (!('Notification' in window)) {
            console.warn('[Firebase] This browser does not support notifications');
            return false;
        }

        // Check if service worker is supported
        if (!('serviceWorker' in navigator)) {
            console.warn('[Firebase] Service workers not supported');
            return false;
        }

        messaging = getMessaging(app);
        console.info('[Firebase] Initialized successfully');
        return true;
    } catch (error) {
        console.error('[Firebase] Initialization error:', error);
        return false;
    }
};

/**
 * Get the Firebase messaging instance
 */
export const getFirebaseMessaging = () => messaging;

/**
 * Check if Firebase messaging is available and ready
 */
export const isMessagingAvailable = () => {
    return messaging !== null && 'Notification' in window && 'serviceWorker' in navigator;
};

/**
 * Register a callback to be called when the FCM token is refreshed
 * @param {Function} callback - Function to call with new token
 */
export const onTokenRefresh = (callback) => {
    if (typeof callback === 'function') {
        tokenRefreshCallbacks.push(callback);
    }
};

/**
 * Notify all registered callbacks about a new token
 * @param {string} newToken - The new FCM token
 */
const notifyTokenRefresh = (newToken) => {
    tokenRefreshCallbacks.forEach(callback => {
        try {
            callback(newToken);
        } catch (error) {
            console.error('[Firebase] Token refresh callback error:', error);
        }
    });
};

/**
 * Get the current FCM token, requesting notification permission if needed
 * This also handles token refresh - if the token has changed, callbacks are notified
 *
 * @returns {Promise<{status: string, token?: string, error?: Error}>}
 */
export const askForPermissionToReceiveNotifications = async () => {
    console.log('[Firebase] askForPermissionToReceiveNotifications called');
    console.log('[Firebase] messaging:', messaging ? 'initialized' : 'null');

    if (!messaging) {
        console.error('[Firebase] Messaging not initialized');
        return {
            status: "FAIL",
            error: new Error("Firebase messaging not initialized")
        };
    }

    if (!process.env.REACT_APP_FIREBASE_VAPID_KEY) {
        console.error('[Firebase] VAPID key not configured');
        return {
            status: "FAIL",
            error: new Error("VAPID key not configured")
        };
    }

    try {
        // Request notification permission if not granted
        console.log('[Firebase] Requesting notification permission...');
        const permission = await Notification.requestPermission();
        console.log('[Firebase] Permission result:', permission);

        if (permission !== 'granted') {
            return {
                status: "FAIL",
                error: new Error(`Notification permission ${permission}`)
            };
        }

        // Register the Firebase messaging service worker explicitly
        // This is needed because the general PWA service worker may not be registered in dev mode
        console.log('[Firebase] Registering Firebase messaging service worker...');
        let swRegistration;
        try {
            swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
                scope: '/'
            });
            console.log('[Firebase] Firebase SW registered:', swRegistration.scope);

            // Wait for the service worker to be ready/active
            if (swRegistration.installing) {
                console.log('[Firebase] SW is installing, waiting for it to activate...');
                await new Promise((resolve) => {
                    swRegistration.installing.addEventListener('statechange', (e) => {
                        if (e.target.state === 'activated') {
                            console.log('[Firebase] SW activated');
                            resolve();
                        }
                    });
                });
            } else if (swRegistration.waiting) {
                console.log('[Firebase] SW is waiting, activating...');
                swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
                await new Promise(resolve => setTimeout(resolve, 100));
            }
        } catch (swError) {
            console.error('[Firebase] SW registration failed:', swError);
            // Fallback: try to use any existing service worker
            swRegistration = await navigator.serviceWorker.ready;
        }
        console.log('[Firebase] Service worker ready:', swRegistration.scope);

        // Get the FCM token
        console.log('[Firebase] Getting FCM token...');
        const token = await getToken(messaging, {
            vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
            serviceWorkerRegistration: swRegistration
        });

        console.log('[Firebase] Token received:', token ? 'yes (length: ' + token.length + ')' : 'no');

        if (!token) {
            return {
                status: "FAIL",
                error: new Error("Failed to get FCM token")
            };
        }

        // Check if token has changed and notify callbacks
        const storedToken = localStorage.getItem('fcm_token');
        if (storedToken && storedToken !== token) {
            console.info('[Firebase] Token refreshed, notifying callbacks');
            notifyTokenRefresh(token);
        }

        // Store token locally for comparison
        localStorage.setItem('fcm_token', token);
        localStorage.setItem('fcm_token_timestamp', Date.now().toString());

        return {
            status: "SUCCESS",
            token: token
        };
    } catch (error) {
        console.error('[Firebase] Error getting token:', error);
        return {
            status: "FAIL",
            error: error
        };
    }
};

/**
 * Force refresh the FCM token by deleting and re-requesting it
 * Useful for ensuring tokens are still valid
 *
 * @returns {Promise<{status: string, token?: string, error?: Error}>}
 */
export const refreshFCMToken = async () => {
    if (!messaging) {
        return {
            status: "FAIL",
            error: new Error("Firebase messaging not initialized")
        };
    }

    try {
        // Delete the current token
        await deleteToken(messaging);
        console.info('[Firebase] Deleted old token');

        // Clear stored token
        localStorage.removeItem('fcm_token');

        // Get a fresh token
        const result = await askForPermissionToReceiveNotifications();

        if (result.status === "SUCCESS") {
            console.info('[Firebase] Got fresh token');
            notifyTokenRefresh(result.token);
        }

        return result;
    } catch (error) {
        console.error('[Firebase] Error refreshing token:', error);
        return {
            status: "FAIL",
            error: error
        };
    }
};

/**
 * Check if the stored token is potentially stale and should be refreshed
 * Tokens should be refreshed periodically to ensure they're still valid
 *
 * @param {number} maxAgeMs - Maximum age in milliseconds (default: 7 days)
 * @returns {boolean} - True if token should be refreshed
 */
export const shouldRefreshToken = (maxAgeMs = 7 * 24 * 60 * 60 * 1000) => {
    const timestamp = localStorage.getItem('fcm_token_timestamp');
    if (!timestamp) return true;

    const age = Date.now() - parseInt(timestamp, 10);
    return age > maxAgeMs;
};

/**
 * Validate and refresh token if needed
 * Call this periodically (e.g., on app startup) to ensure tokens stay valid
 *
 * @param {Function} onNewToken - Callback to send new token to backend
 * @returns {Promise<{status: string, token?: string, refreshed: boolean}>}
 */
export const validateAndRefreshToken = async (onNewToken) => {
    if (!messaging) {
        return { status: "FAIL", error: new Error("Messaging not initialized"), refreshed: false };
    }

    const currentToken = localStorage.getItem('fcm_token');
    const needsRefresh = shouldRefreshToken();

    if (!currentToken || needsRefresh) {
        console.info('[Firebase] Token needs refresh (missing or stale)');
        const result = await askForPermissionToReceiveNotifications();

        if (result.status === "SUCCESS" && onNewToken && result.token !== currentToken) {
            try {
                await onNewToken(result.token);
            } catch (error) {
                console.error('[Firebase] Error sending new token to backend:', error);
            }
        }

        return {
            status: result.status,
            token: result.token,
            refreshed: true
        };
    }

    return {
        status: "SUCCESS",
        token: currentToken,
        refreshed: false
    };
};

/**
 * Delete the FCM token (e.g., when user logs out or disables notifications)
 *
 * @returns {Promise<boolean>}
 */
export const deleteFCMToken = async () => {
    if (!messaging) return false;

    try {
        await deleteToken(messaging);
        localStorage.removeItem('fcm_token');
        localStorage.removeItem('fcm_token_timestamp');
        localStorage.removeItem('token'); // Legacy token storage
        console.info('[Firebase] Token deleted');
        return true;
    } catch (error) {
        console.error('[Firebase] Error deleting token:', error);
        return false;
    }
};

/**
 * Setup foreground message handler
 *
 * @param {Function} handler - Function to call when message received
 * @returns {Function} - Unsubscribe function
 */
export const setupForegroundMessageHandler = (handler) => {
    if (!messaging) {
        console.warn('[Firebase] Cannot setup message handler - messaging not initialized');
        return () => {};
    }

    return onMessage(messaging, (payload) => {
        console.info('[Firebase] Foreground message received:', payload);
        if (handler) {
            handler(payload);
        }
    });
};

/**
 * Get diagnostic info about the current Firebase/FCM state
 * Useful for debugging
 *
 * @returns {Object}
 */
export const getDiagnostics = () => {
    return {
        firebaseInitialized: app !== null,
        messagingAvailable: messaging !== null,
        notificationSupport: 'Notification' in window,
        serviceWorkerSupport: 'serviceWorker' in navigator,
        notificationPermission: 'Notification' in window ? Notification.permission : 'unavailable',
        storedToken: localStorage.getItem('fcm_token') ? 'present' : 'missing',
        tokenTimestamp: localStorage.getItem('fcm_token_timestamp'),
        tokenAge: localStorage.getItem('fcm_token_timestamp')
            ? `${Math.round((Date.now() - parseInt(localStorage.getItem('fcm_token_timestamp'), 10)) / 1000 / 60)} minutes`
            : 'unknown',
        vapidKeyConfigured: !!process.env.REACT_APP_FIREBASE_VAPID_KEY,
    };
};
